import { bookMeta, queryMeta, shareQueryText, wrapReadOnly } from "../sqlfile";
import { parseTable } from "../sample";
import { format as formatSqlText } from "sql-formatter";
import { renderHome } from "./home";
import * as reports from "./reports";
import { ago, api, clipboardFiles, dateOf, esc, icon, isTyping, pastedFile, setPageMeta, toLogin, toast, upload, writeClipboard } from "./util";
import { STYLE_NAMES, savedStyle, setStyle } from "./theme-loader"; // first: applies the look and light/dark before anything renders

type Query = { id: number; title: string; notes: string; sql: string; tags: string; pinned: number; created_at: string; updated_at: string; samples?: number };
type Sample = { id: number; query_id: number; name: string; type: string; size: number; created_at: string };

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const el = {
  list: $("list"), search: $<HTMLInputElement>("search"), tags: $("tags"), empty: $("empty"), editor: $("editor"), count: $("count"),
  title: $<HTMLInputElement>("title"), tagsInput: $<HTMLInputElement>("tagsInput"), notes: $<HTMLTextAreaElement>("notes"),
  sql: $<HTMLTextAreaElement>("sql"), hl: $("hl"), gutter: $("gutter"), lines: $("lines"), status: $("status"), meta: $("meta"),
  readonly: $<HTMLInputElement>("readonly"), pin: $("pin"), del: $("del"),
  samples: $("samples"), sampleTabs: $("sampleTabs"), sampleBar: $("sampleBar"), sampleView: $("sampleView"),
  sampleDrop: $("sampleDrop"), sampleCount: $("sampleCount"),
};

let queries: Query[] = [];
let current: Query | null = null;
let dirty = false;
let edits = 0; // bumped on every keystroke, so a save only clears "dirty" if nothing changed while it was in flight
let tagFilter = "";
let saveTimer: ReturnType<typeof setTimeout> | undefined;

function bytes(n: number) {
  return n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/* ---------- api ---------- */
const load = async () => { queries = await api<Query[]>("/api/queries"); };

/* ---------- sidebar ---------- */
const tagsOf = (q: Query) => q.tags.split(",").map((t) => t.trim()).filter(Boolean);
const firstLine = (s: string) => s.split("\n").find((l) => l.trim())?.trim() ?? "";

/** The sidebar shows either queries or reports; the main area shows whichever one is open. */
type Mode = "queries" | "reports";
let mode: Mode = "queries";
try { if (localStorage.getItem("qb.mode") === "reports") mode = "reports"; } catch {}
function setMode(m: Mode) {
  applyMode(m);
  renderList();
  if (!current && !reports.openId()) showEmpty();
}
/** The switch, the search box and the + button follow the mode. */
function applyMode(m: Mode) {
  mode = m;
  try { localStorage.setItem("qb.mode", m); } catch {}
  document.body.classList.toggle("mode-reports", m === "reports");
  for (const b of document.querySelectorAll<HTMLElement>("#sideTabs [data-mode]")) b.setAttribute("aria-selected", String(b.dataset.mode === m));
  el.search.placeholder = m === "reports" ? "Search reports" : "Search queries";
  $("new").title = m === "reports" ? "New report" : "New query";
  $("new").setAttribute("aria-label", $("new").title);
}

function renderList() {
  const term = el.search.value.trim().toLowerCase();
  if (mode === "reports") {
    el.count.textContent = reports.list.length ? String(reports.list.length) : "";
    el.list.innerHTML = reports.listHtml(term);
    return;
  }
  const shown = queries.filter((q) =>
    (!tagFilter || tagsOf(q).includes(tagFilter)) &&
    (!term || [q.title, q.notes, q.sql, q.tags].some((f) => f.toLowerCase().includes(term))));
  el.count.textContent = queries.length ? String(queries.length) : "";
  el.list.innerHTML = shown.map((q) => {
    const tags = tagsOf(q);
    const meta = (tags.length ? `<span class="tg">${esc(tags.map((t) => `#${t}`).join(" "))}</span>` : "") +
      (q.samples ? `<span class="sm" title="${q.samples} sample output${q.samples > 1 ? "s" : ""}">${icon("table")}${q.samples}</span>` : "");
    return `<button class="item${q.id === current?.id ? " active" : ""}" data-id="${q.id}">
      <span class="t">${q.pinned ? icon("star", "i pin") : ""}<span class="name">${esc(q.title)}</span><span class="when">${ago(q.updated_at)}</span></span>
      <span class="s">${esc(firstLine(q.notes) || firstLine(q.sql)) || "&nbsp;"}</span>
      <span class="m">${meta}</span>
    </button>`;
  }).join("") || `<div class="none">${queries.length ? "No query matches." : "No queries yet."}</div>`;

  // tags: most used first, with counts; past TAG_FOLD they fold behind "+N more" (the picked one always shows)
  const counts = new Map<string, number>();
  for (const t of queries.flatMap(tagsOf)) counts.set(t, (counts.get(t) ?? 0) + 1);
  const all = [...counts.keys()].sort((a, b) => counts.get(b)! - counts.get(a)! || a.localeCompare(b));
  if (tagFilter && !counts.has(tagFilter)) tagFilter = "";
  const folds = all.length > TAG_FOLD + 1;
  let shownTags = folds && !tagsOpen ? all.slice(0, TAG_FOLD) : all;
  if (tagFilter && !shownTags.includes(tagFilter)) shownTags = [...shownTags, tagFilter];
  const chip = (cls: string, html: string, title: string, onclick: () => void) => {
    const c = document.createElement("button");
    c.className = `chip${cls}`;
    c.innerHTML = html;
    c.title = title;
    c.onclick = onclick;
    return c;
  };
  el.tags.replaceChildren(
    ...shownTags.map((t) => chip(t === tagFilter ? " on" : "", `${esc(t)}<span class="n">${counts.get(t)}</span>`,
      `${counts.get(t)} quer${counts.get(t) === 1 ? "y" : "ies"} tagged ${t}`,
      () => { tagFilter = tagFilter === t ? "" : t; renderList(); })),
    ...(folds ? [chip(" more", tagsOpen ? `Show less${icon("chev", "i chev up")}` : `+${all.length - shownTags.length} more${icon("chev", "i chev")}`, tagsOpen ? "Show only the most used tags" : "Show every tag",
      () => { tagsOpen = !tagsOpen; try { localStorage.setItem("qb.tagsOpen", tagsOpen ? "1" : "0"); } catch {} renderList(); })] : []),
  );
  el.tags.classList.toggle("open", folds && tagsOpen);
}
const TAG_FOLD = 6;
let tagsOpen = false;
try { tagsOpen = localStorage.getItem("qb.tagsOpen") === "1"; } catch {}
el.list.addEventListener("click", (e) => {
  const item = (e.target as HTMLElement).closest<HTMLElement>(".item");
  if (!item) return;
  if (item.dataset.report) reports.open(Number(item.dataset.report));
  else open(Number(item.dataset.id));
});

/* ---------- editor ---------- */
const fitNotes = () => { el.notes.style.height = "auto"; el.notes.style.height = `${Math.min(el.notes.scrollHeight + 2, 320)}px`; };
async function open(id: number, edit = false) {
  if (dirty) await save();
  await reports.close();
  const prev = current?.id;
  current = queries.find((q) => q.id === id) ?? null;
  if (!current) return showEmpty();
  el.empty.hidden = true;
  el.editor.hidden = false;
  el.title.value = current.title;
  el.tagsInput.value = current.tags;
  el.notes.value = current.notes;
  el.sql.value = current.sql;
  fitNotes();
  setDirty(false);
  disarmDelete();
  setEditing(edit);
  renderEditorBits();
  highlight();
  history.replaceState(null, "", `/q/${id}`);
  setPageMeta(queryMeta(current));
  renderList();
  if (prev !== id) {
    samples = [];
    activeSample = null;
    renderSamples();
    loadSamples();
    document.querySelector(".main")!.scrollTop = 0;
  }
}

/* ---------- view / edit: a query opens locked so it can't be changed by accident ---------- */
let editing = false;
const editBtn = $("edit");
function setEditing(on: boolean) {
  editing = on;
  el.editor.classList.toggle("locked", !on);
  for (const f of [el.title, el.tagsInput, el.notes, el.sql]) f.readOnly = !on;
  editBtn.classList.toggle("primary", !on);
  editBtn.querySelector("use")!.setAttribute("href", on ? "#i-check" : "#i-edit");
  editBtn.querySelector("span")!.textContent = on ? "Done" : "Edit";
  editBtn.title = on ? "Done editing (E or Esc)" : "Edit (E)";
  if (!on) disarmDelete();
}
async function toggleEditing() {
  if (!current) return;
  if (editing && dirty) await save();
  setEditing(!editing);
  if (editing) el.sql.focus();
  else (document.activeElement as HTMLElement | null)?.blur();
}

/* ---------- format ---------- */
function formatSql() {
  if (!current) return;
  if (!editing) setEditing(true); // formatting is an edit: unlock first (Done or Esc locks again)
  let out: string;
  try {
    out = formatSqlText(el.sql.value, { language: "postgresql", keywordCase: "upper", tabWidth: 2, linesBetweenQueries: 1 })
      .replace(/\b([a-z_][a-z0-9_]*(?:\.[a-z_][a-z0-9_]*)*) \(/g, "$1("); // st_area (x) -> st_area(x): unknown functions get a stray space
  } catch (e) {
    return toast(`Can't format: ${String((e as Error).message ?? e).split("\n")[0]}`);
  }
  if (out.trim() === el.sql.value.trim()) return toast("Already formatted");
  el.sql.focus();
  el.sql.select();
  document.execCommand("insertText", false, out); // one undo step, and fires "input" like typing does
  el.sql.setSelectionRange(0, 0);
  el.sql.scrollTop = 0;
  toast("Formatted (⌘Z to undo)");
}

function showEmpty() {
  current = null;
  el.editor.hidden = true;
  $("report").hidden = true;
  el.empty.hidden = false;
  history.replaceState(null, "", "/");
  setPageMeta(bookMeta(queries));
  renderList();
  const r = mode === "reports";
  $("reportsWelcome").hidden = !r;
  if (r) {
    $("home").hidden = $("welcome").hidden = true;
    el.empty.classList.remove("has-home");
  } else refreshHome();
}

/* ---------- home: the bento grid shown when no query is open ---------- */
const homeHooks = {
  open: (id: number) => open(id),
  newQuery: () => newQuery(),
  filterTag: (t: string) => { tagFilter = t; renderList(); el.search.focus(); },
  copy: async (q: Query) => {
    const ok = await writeClipboard(el.readonly.checked ? wrapReadOnly(q.sql) : `${q.sql.trim()}\n`);
    toast(ok ? `Copied “${q.title}”${el.readonly.checked ? ", wrapped in READ ONLY" : ""}` : "Couldn't copy");
  },
};
/** Re-draws the home tiles (and re-fetches the latest samples) if home is what's showing. */
function refreshHome() {
  if (current || reports.openId() || mode === "reports") return;
  renderHome(queries, homeHooks);
}

function renderEditorBits() {
  if (!current) return;
  el.pin.classList.toggle("on", !!current.pinned);
  el.pin.title = current.pinned ? "Unpin" : "Pin to top";
  el.meta.textContent = `Created ${dateOf(current.created_at).toLocaleString()} · updated ${dateOf(current.updated_at).toLocaleString()}`;
}

function setDirty(d: boolean) {
  dirty = d;
  el.status.textContent = d ? "Unsaved changes" : current ? "Saved" : "";
  el.status.classList.toggle("dirty", d);
  el.status.classList.remove("error");
}

async function save() {
  clearTimeout(saveTimer);
  if (!current || !dirty) return;
  const body = { title: el.title.value, tags: el.tagsInput.value, notes: el.notes.value, sql: el.sql.value };
  const startedAt = edits;
  try {
    const q = await api<Query>(`/api/queries/${current.id}`, { method: "PUT", body: JSON.stringify(body), keepalive: true });
    queries = queries.map((x) => (x.id === q.id ? { ...x, ...q } : x));
    if (current?.id === q.id) {
      current = { ...current, ...q };
      setPageMeta(queryMeta(current));
    }
    if (edits === startedAt) setDirty(false);
    renderEditorBits();
    await load();
    renderList();
  } catch (e) {
    el.status.textContent = "Save failed";
    el.status.classList.add("error");
    toast(String(e));
  }
}

function changed() {
  edits++;
  setDirty(true);
  clearTimeout(saveTimer);
  saveTimer = setTimeout(save, 800);
}

/* ---------- SQL highlighting (display only) ---------- */
const KEYWORDS = new Set(`select from where and or not in is null as on join left right inner outer full cross using group by order
having limit offset with case when then else end distinct union all except intersect insert update delete into values set
create table view index drop alter begin transaction read only rollback commit filter over partition within asc desc
exists between like ilike true false nulls first last any some returning lateral recursive materialized`.split(/\s+/));
const TOKEN = /(\/\*[\s\S]*?(?:\*\/|$))|(--[^\n]*)|('(?:[^']|'')*'?)|(\b\d+(?:\.\d+)?\b)|([A-Za-z_][A-Za-z0-9_]*)(?=(\s*\())?/g;

function highlight() {
  const src = el.sql.value;
  let out = "", last = 0;
  for (const m of src.matchAll(TOKEN)) {
    out += esc(src.slice(last, m.index));
    const [tok, block, line, str, num, word, call] = m;
    const cls = block || line ? "c" : str ? "s" : num ? "n" : word && KEYWORDS.has(word.toLowerCase()) ? "k" : word && call !== undefined ? "f" : "";
    out += cls ? `<span class="${cls}">${esc(tok)}</span>` : esc(tok);
    last = m.index! + tok.length;
  }
  el.hl.innerHTML = out + esc(src.slice(last)) + "\n";
  const n = src.split("\n").length;
  if (el.gutter.dataset.n !== String(n)) {
    el.gutter.dataset.n = String(n);
    el.gutter.textContent = Array.from({ length: n }, (_, i) => i + 1).join("\n") + "\n";
  }
  el.lines.textContent = `${n} line${n === 1 ? "" : "s"}`;
  syncScroll();
}
const syncScroll = () => {
  el.hl.scrollTop = el.gutter.scrollTop = el.sql.scrollTop;
  el.hl.scrollLeft = el.sql.scrollLeft;
};

/* ---------- sample output ---------- */
let samples: Sample[] = [];
let activeSample: number | null = null;
const sampleText = new Map<number, string>();
const MAX_ROWS = 2000;
const NULLISH = new Set(["NULL", "null", "<null>", "[null]", "(null)"]);
const NUMERIC = /^-?\d+(\.\d+)?(e[+-]?\d+)?$/i;

async function loadSamples() {
  if (!current) return;
  const id = current.id;
  let list: Sample[];
  try { list = await api<Sample[]>(`/api/queries/${id}/samples`); }
  catch (e) { return toast(`Couldn't load sample output: ${e}`); }
  if (current?.id !== id) return;
  samples = list;
  if (!samples.some((s) => s.id === activeSample)) activeSample = samples.at(-1)?.id ?? null;
  renderSamples();
}

function renderSamples() {
  const n = samples.length;
  el.sampleCount.textContent = n ? String(n) : "";
  el.sampleDrop.hidden = n > 0;
  el.sampleTabs.innerHTML = n > 1
    ? samples.map((s) => `<button class="stab" role="tab" data-id="${s.id}" aria-selected="${s.id === activeSample}" title="${esc(s.name)}">${icon(kindIcon(s))}<span>${esc(s.name)}</span></button>`).join("")
    : "";
  renderSampleView();
}
const kindIcon = (s: Sample) => (s.type.startsWith("image/") ? "image" : s.type === "text/plain" ? "text" : "table");

async function renderSampleView() {
  const s = samples.find((x) => x.id === activeSample);
  if (!s) { el.sampleBar.innerHTML = el.sampleView.innerHTML = ""; return; }
  const n1 = samples.length > 1; // with tabs showing, the bar doesn't repeat the name
  const bar = (info: string) => {
    el.sampleBar.innerHTML = `${n1 ? "" : `${icon(kindIcon(s))}<strong>${esc(s.name)}</strong><span class="dot"></span>`}${info}
      <span class="dot">${bytes(s.size)}</span><span class="dot">added ${esc(ago(s.created_at))}</span><span class="spacer"></span>
      <button class="btn ghost small" data-to-report="${s.id}" title="Add this sample to a report for people outside the team">${icon("report")}Add to report</button>
      <a class="btn ghost small" href="/api/samples/${s.id}" download="${esc(s.name)}">${icon("download")}Download</a>
      <button class="btn ghost small danger" data-del-sample="${s.id}" data-edit-only>${icon("trash")}<span>Remove</span></button>`;
  };

  if (s.type.startsWith("image/")) {
    bar("Screenshot");
    el.sampleView.innerHTML = `<img src="/api/samples/${s.id}" alt="${esc(s.name)}" />`;
    return;
  }
  if (!sampleText.has(s.id)) {
    bar("");
    el.sampleView.innerHTML = `<div class="loading">Loading…</div>`;
    const res = await fetch(`/api/samples/${s.id}`);
    if (res.status === 401) return toLogin();
    sampleText.set(s.id, await res.text());
    if (activeSample !== s.id) return;
  }
  const text = sampleText.get(s.id)!;
  const t = parseTable(text, s.name);
  if (!t) {
    bar(`${text.split("\n").length} lines of text`);
    el.sampleView.innerHTML = `<pre>${esc(text)}</pre>`;
    return;
  }
  bar(`${t.rows.length.toLocaleString()} row${t.rows.length === 1 ? "" : "s"} × ${t.columns.length} column${t.columns.length === 1 ? "" : "s"}`);
  const rows = t.rows.slice(0, MAX_ROWS);
  const isNull = (v: string | null) => v === null || NULLISH.has(v);
  const numeric = t.columns.map((_, c) => {
    const vals = rows.map((r) => r[c]).filter((v): v is string => !isNull(v ?? null) && v !== "");
    return vals.length > 0 && vals.every((v) => NUMERIC.test(v));
  });
  const cell = (v: string | null, c: number) =>
    isNull(v) ? `<td class="null">NULL</td>` : `<td${numeric[c] ? ' class="num"' : ""} title="${esc(v!)}">${esc(v!)}</td>`;
  el.sampleView.innerHTML = `<table class="grid">
    <thead><tr><th class="rn">#</th>${t.columns.map((c) => `<th title="${esc(c)}">${esc(c)}</th>`).join("")}</tr></thead>
    <tbody>${rows.map((r, i) => `<tr><td class="rn">${i + 1}</td>${r.map(cell).join("")}</tr>`).join("")}</tbody>
  </table>${t.rows.length > MAX_ROWS ? `<div class="grid-more">Showing the first ${MAX_ROWS.toLocaleString()} rows. Download to see all ${t.rows.length.toLocaleString()}.</div>` : ""}`;
}

async function addSamples(files: File[]) {
  if (!current || !files.length) return;
  if (dirty) await save();
  const added = await upload<Sample[]>(`/api/queries/${current.id}/samples`, files);
  if (!added) return;
  activeSample = added.at(-1)!.id;
  await loadSamples();
  await load();
  renderList();
  toast(added.length > 1 ? `Added ${added.length} samples` : `Added ${added[0]!.name}`);
}

async function pasteFromClipboard() {
  const files = await clipboardFiles();
  if (files) await addSamples(files);
}

let sampleDeleteArmed: number | null = null;
async function deleteSample(id: number, btn: HTMLElement) {
  if (sampleDeleteArmed !== id) {
    sampleDeleteArmed = id;
    btn.classList.add("armed");
    btn.querySelector("span")!.textContent = "Click again to remove";
    setTimeout(() => { if (sampleDeleteArmed === id) { sampleDeleteArmed = null; renderSampleView(); } }, 3000);
    return;
  }
  sampleDeleteArmed = null;
  await api(`/api/samples/${id}`, { method: "DELETE" });
  sampleText.delete(id);
  samples = samples.filter((s) => s.id !== id);
  if (activeSample === id) activeSample = samples.at(-1)?.id ?? null;
  renderSamples();
  await load();
  renderList();
  toast("Sample removed");
}

/* ---------- actions ---------- */
async function newQuery(from?: Partial<Query>) {
  if (dirty) await save();
  const q = await api<Query>("/api/queries", { method: "POST", body: JSON.stringify(from ?? { title: "Untitled query", sql: "SELECT\n  \nFROM mlmupc.parcel pc\nWHERE pc.deleted_at IS NULL\nLIMIT 100;" }) });
  await load();
  await open(q.id, true);
  el.title.focus();
  el.title.select();
}

async function copySql() {
  const sql = el.sql.value.trim();
  if (!sql) return toast("Nothing to copy");
  if (!(await writeClipboard(el.readonly.checked ? wrapReadOnly(sql) : `${sql}\n`))) return toast("Couldn't copy. Select the SQL and press ⌘C.");
  toast(el.readonly.checked ? "Copied, wrapped in READ ONLY" : "Copied");
}

let deleteArmed: ReturnType<typeof setTimeout> | undefined;
const delLabel = el.del.querySelector(".del-label")!;
function disarmDelete() { clearTimeout(deleteArmed); deleteArmed = undefined; el.del.classList.remove("armed"); delLabel.textContent = ""; }
async function del() {
  if (!current) return;
  if (!deleteArmed) {
    el.del.classList.add("armed");
    delLabel.textContent = "Click again to delete";
    deleteArmed = setTimeout(disarmDelete, 3000);
    return;
  }
  disarmDelete();
  const id = current.id;
  setDirty(false);
  await api(`/api/queries/${id}`, { method: "DELETE" });
  await load();
  toast("Deleted");
  showEmpty();
}

async function togglePin() {
  if (!current) return;
  await save();
  const q = await api<Query>(`/api/queries/${current.id}`, { method: "PUT", body: JSON.stringify({ pinned: current.pinned ? 0 : 1 }) });
  current = { ...current, ...q };
  await load();
  renderEditorBits();
  renderList();
}

async function importFile(file: File) {
  const r = await api<{ added: number; skipped: number }>("/api/import", { method: "POST", body: await file.text(), headers: { "content-type": "text/plain" } });
  await load();
  renderList();
  refreshHome();
  toast(`Imported ${r.added}${r.skipped ? `, skipped ${r.skipped} with a title that already exists` : ""}`);
}

/* ---------- wiring ---------- */
for (const input of [el.title, el.tagsInput, el.notes]) input.addEventListener("input", changed);
el.notes.addEventListener("input", fitNotes);
el.sql.addEventListener("input", () => { highlight(); changed(); });
el.sql.addEventListener("scroll", syncScroll);
el.sql.addEventListener("keydown", (e) => {
  if (e.key !== "Tab" || e.metaKey || e.ctrlKey) return;
  e.preventDefault();
  document.execCommand("insertText", false, "  "); // keeps undo history
});
el.search.addEventListener("input", renderList);
el.search.addEventListener("keydown", (e) => { if (e.key === "Escape") { el.search.value = ""; renderList(); el.search.blur(); } });
$("new").onclick = () => (mode === "reports" ? reports.create() : newQuery());
$("newReportEmpty").onclick = () => reports.create();
$("sideTabs").addEventListener("click", (e) => {
  const m = (e.target as HTMLElement).closest<HTMLElement>("[data-mode]")?.dataset.mode as Mode | undefined;
  if (m && m !== mode) setMode(m);
});
reports.init({
  leaveQuery: async () => {
    if (dirty) await save();
    current = null;
    el.editor.hidden = true;
    if (mode !== "reports") setMode("reports");
  },
  showEmpty,
  renderList,
});
$("newEmpty").onclick = () => newQuery();
$("save").onclick = () => { setDirty(true); save(); };
$("copy").onclick = copySql;
$("dup").onclick = () => current && newQuery({ title: `${el.title.value} (copy)`, notes: el.notes.value, sql: el.sql.value, tags: el.tagsInput.value });
el.del.onclick = del;
el.pin.onclick = togglePin;
$<HTMLInputElement>("import").onchange = (e) => {
  const input = e.target as HTMLInputElement;
  if (input.files?.[0]) importFile(input.files[0]);
  input.value = "";
};

// sample output: upload, paste, drag and drop, tabs, remove
$<HTMLInputElement>("sampleFile").onchange = (e) => {
  const input = e.target as HTMLInputElement;
  addSamples([...(input.files ?? [])]);
  input.value = "";
};
$("pasteSample").onclick = pasteFromClipboard;
el.sampleTabs.addEventListener("click", (e) => {
  const tab = (e.target as HTMLElement).closest<HTMLElement>(".stab");
  if (!tab) return;
  activeSample = Number(tab.dataset.id);
  renderSamples();
});
el.sampleBar.addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-del-sample]");
  if (btn) deleteSample(Number(btn.dataset.delSample), btn);
  const add = (e.target as HTMLElement).closest<HTMLElement>("[data-to-report]");
  if (add) reports.pickFor(Number(add.dataset.toReport), add);
});
let dragDepth = 0;
const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes("Files");
el.samples.addEventListener("dragenter", (e) => { if (hasFiles(e)) { dragDepth++; el.samples.classList.add("dragging"); } });
el.samples.addEventListener("dragleave", () => { if (--dragDepth <= 0) { dragDepth = 0; el.samples.classList.remove("dragging"); } });
el.samples.addEventListener("dragover", (e) => { if (hasFiles(e)) e.preventDefault(); });
el.samples.addEventListener("drop", (e) => {
  e.preventDefault();
  dragDepth = 0;
  el.samples.classList.remove("dragging");
  addSamples([...(e.dataTransfer?.files ?? [])]);
});
// ⌘V outside a text field adds the clipboard as a sample
document.addEventListener("paste", (e) => {
  if (!current || isTyping(e.target)) return;
  const files = [...(e.clipboardData?.files ?? [])];
  const text = e.clipboardData?.getData("text/plain") ?? "";
  if (!files.length && !text.trim()) return;
  e.preventDefault();
  addSamples(files.length ? files : [pastedFile(text)]);
});

/* ---------- settings panel (gear in the sidebar footer): look, light/dark, log out ---------- */
const LOOK_NOTES: Record<string, string> = {
  blueprint: "Drafting sheet, blue and amber",
  modern: "Clean and quiet",
  editorial: "Newspaper: serif headlines, red ink",
  terminal: "Green phosphor screen",
  retro: "Cream paper, ink borders",
  pacman: "Arcade maze",
};
const settingsBtn = $("settingsBtn");
const settingsPop = $("settings");
const lookList = $("lookList");
const logoutBtn = $("logout");
let looks: string[] = [];
let defaultLook = "blueprint";

function renderSettings() {
  const look = savedStyle() ?? defaultLook;
  lookList.innerHTML = looks.map((k) => `<button class="sp-look" data-look="${k}" aria-pressed="${k === look}">
      <span><strong>${esc(STYLE_NAMES[k] ?? k)}</strong><small>${esc(LOOK_NOTES[k] ?? "")}${k === defaultLook ? " · default" : ""}</small></span>
      ${k === look ? icon("check") : ""}</button>`).join("");
  const theme = document.documentElement.dataset.theme ?? "system";
  for (const b of settingsPop.querySelectorAll<HTMLElement>("[data-theme-pick]")) b.setAttribute("aria-pressed", String(b.dataset.themePick === theme));
}
function toggleSettings(open = settingsPop.hidden) {
  settingsPop.hidden = !open;
  settingsBtn.setAttribute("aria-expanded", String(open));
  if (open) renderSettings();
}
settingsBtn.onclick = (e) => { e.stopPropagation(); toggleSettings(); };
// composedPath, not contains(): picking a look redraws the list, so the clicked button is already detached by now
document.addEventListener("click", (e) => { if (!settingsPop.hidden && !e.composedPath().includes(settingsPop)) toggleSettings(false); });
document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !settingsPop.hidden) { toggleSettings(false); settingsBtn.focus(); } }, true);

lookList.addEventListener("click", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>("[data-look]");
  if (!b) return;
  setStyle(b.dataset.look!);
  renderSettings();
});
settingsPop.querySelector("#themeSeg")!.addEventListener("click", (e) => {
  const t = (e.target as HTMLElement).closest<HTMLElement>("[data-theme-pick]")?.dataset.themePick;
  if (!t) return;
  if (t === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = t;
  try { t === "system" ? localStorage.removeItem("qb.theme") : localStorage.setItem("qb.theme", t); } catch {}
  renderSettings();
});

fetch("/api/session").then((r) => r.json()).then((s: { auth: boolean; styles: string[]; defaultStyle: string }) => {
  looks = s.styles;
  defaultLook = s.defaultStyle;
  logoutBtn.hidden = !s.auth;
}).catch(() => {});
logoutBtn.onclick = async () => {
  if (dirty) await save();
  await fetch("/api/logout", { method: "POST" });
  toLogin();
};

// the notes box is sized to its text, so measure again once the look and its fonts are in, and on resize
for (const ev of ["qb:style", "resize"]) window.addEventListener(ev, () => { if (current) fitNotes(); });
document.fonts?.ready.then(() => { if (current) fitNotes(); });

editBtn.onclick = toggleEditing;
$("format").onclick = formatSql;

try { el.readonly.checked = localStorage.getItem("qb.readonly") !== "0"; } catch {}
el.readonly.onchange = () => { try { localStorage.setItem("qb.readonly", el.readonly.checked ? "1" : "0"); } catch {} };

document.addEventListener("keydown", (e) => {
  const mod = e.metaKey || e.ctrlKey;
  if (mod && e.key === "s") { e.preventDefault(); if (reports.openId()) reports.flush(); else { setDirty(true); save(); } }
  else if (mod && e.shiftKey && e.key.toLowerCase() === "c" && current) { e.preventDefault(); copySql(); }
  else if (e.key === "/" && !isTyping(e.target)) { e.preventDefault(); el.search.focus(); }
  else if (current && e.shiftKey && e.altKey && e.code === "KeyF") { e.preventDefault(); formatSql(); }
  else if (current && !mod && !e.altKey && e.key.toLowerCase() === "e" && !isTyping(e.target)) { e.preventDefault(); toggleEditing(); }
  else if (current && editing && e.key === "Escape" && e.target !== el.search) { e.preventDefault(); toggleEditing(); }
});
window.addEventListener("pagehide", () => { if (dirty) save(); });
setInterval(renderList, 60_000); // keeps the "5m ago" times fresh

// shared links use /q/<id> or /r/<id> (so link previews get server-rendered meta tags); old /#<id> and /?q=<id> links still work
$("share").onclick = async () => {
  if (!current) return;
  const url = `${location.origin}/q/${current.id}`;
  const text = shareQueryText({ title: el.title.value, tags: el.tagsInput.value, notes: el.notes.value, sql: el.sql.value }, url);
  toast((await writeClipboard(text)) ? "Copied title, tags, query & link." : `Couldn't copy: ${url}`);
};
function targetFromUrl(): { kind: "query" | "report"; id: number } | null {
  const path = /^\/(q|r)\/(\d+)\/?$/.exec(location.pathname);
  if (path) return { kind: path[1] === "r" ? "report" : "query", id: Number(path[2]) };
  const params = new URLSearchParams(location.search);
  if (params.get("r")) return { kind: "report", id: Number(params.get("r")) };
  if (params.get("q")) return { kind: "query", id: Number(params.get("q")) };
  const hr = /^#r(\d+)$/.exec(location.hash);
  if (hr) return { kind: "report", id: Number(hr[1]) };
  const hq = /^#(\d+)$/.exec(location.hash);
  if (hq) return { kind: "query", id: Number(hq[1]) };
  return null;
}
async function openFromUrl() {
  const t = targetFromUrl();
  if (!t) return;
  if (t.kind === "report") {
    if (t.id === reports.openId()) return;
    if (mode !== "reports") setMode("reports");
    return reports.open(t.id);
  }
  if (!t.id || t.id === current?.id) return;
  if (queries.some((q) => q.id === t.id)) {
    await open(t.id);
    if (mode !== "queries") setMode("queries");
    return;
  }
  toast("That query doesn't exist any more");
  showEmpty();
}
window.addEventListener("hashchange", openFromUrl);
window.addEventListener("popstate", openFromUrl);

await Promise.all([load(), reports.load()]);
const initial = targetFromUrl();
applyMode(initial ? (initial.kind === "report" ? "reports" : "queries") : mode);
if (initial) await openFromUrl();
else { setMode(mode); setPageMeta(bookMeta(queries)); }
setInterval(refreshHome, 60_000); // keeps "5m ago" fresh on the home tiles too
